import { useAppAlert } from '@/app/AppAlertProvider'
import LoadingCard from '@/app/common/components/LoadingCard'
import { FamilyApplicationQueries } from '@/queries/family_applications/queries'
import { AppRoutePaths } from '@/routing/route_paths'
import {
    selectFamilyApplication,
    UpdateFamilyAttestation,
} from '@/storage/applications/familyApplicationSlice'
import { selectProgramID, selectProgramSettings } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch, RootState } from '@/storage/store'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import sanitizedHtml from 'sanitize-html'
import FamilyWizardLayout from '../../FamilyWizardLayout'
import { SubmitButton } from '../../student_details/shared/submit-button'
import { ATTESTATION_STATUS } from '../../utils'


function TaxCommissionAttestation() {
    const { t } = useTranslation()
    const applications = useSelector(selectFamilyApplication)
    const navigate = useNavigate()
    const programSettings = useSelector(selectProgramSettings)
    const programId = useSelector(selectProgramID)
    const { data: attestationTexts, status } = useQuery(
        FamilyApplicationQueries.fetchAcctAttestationsText(programId, {
            staleTime: Infinity,
        })
    )
    const familyAppRequest = useSelector(
        (state: RootState) => state.FamilyApplication
    )

    const { fireAlert } = useAppAlert()
    const dispatch = useDispatch<AppDispatch>()

    async function handleAgreeClick(attestationId: number) {
        const actionResult = await dispatch(
            UpdateFamilyAttestation({
                family_account_id: applications.family_account_id,
                attestation: {
                    id: attestationId,
                    status: ATTESTATION_STATUS.CONFIRMED,
                },
            })
        )
        if (UpdateFamilyAttestation.fulfilled.match(actionResult)) {
            navigate(AppRoutePaths.family_application.income_verification.tax_commission_confirmation)
        } else {
            fireAlert({
                variant: 'error',
                message: actionResult.payload?.message ?? '',
            })
        }
    }

    if (status === 'pending') {
        return (
            <LoadingCard>
                <p>{t('loading')}...</p>
            </LoadingCard>
        )
    }

    if (status === 'error') {
        return <div>{t('error')}</div>
    }

    const TaxCommissionAttestation = applications.attestations
        .filter((att) => att.step === 'income')
        .map((att) => att.id)

    const incomeAttestationTexts = attestationTexts.filter((text) => {
        return TaxCommissionAttestation.includes(text.id)
    })

    const sanitizeText = (text: string) => {
        const defaultOptions = {
            allowedAttributes: {
                a: ['href', 'class', 'target'],
            },
        }

        return (
            <p
                className="text-gray-700  text-sm"
                dangerouslySetInnerHTML={{
                    __html: sanitizedHtml(text.replace("{{year}}", programSettings.acceptable_income_year.toString()), defaultOptions),
                }}
            ></p>
        )
    }

    return incomeAttestationTexts.map((att) => {
        const firstSection = att.attestation_text[0]
        const secondSection = att.attestation_text[1]
        return (
            <FamilyWizardLayout>
                <FamilyWizardLayout.Header>
                    {t('tax_commission_acknowledgement')}
                </FamilyWizardLayout.Header>
                <FamilyWizardLayout.Content>
                    <main>
                        <div className="space-y-8">
                            {firstSection && (
                                <section>
                                    <h3 className="font-medium mb-2">
                                        {t('i_acknowledge_that')}:
                                    </h3>
                                    {sanitizeText(firstSection)}
                                </section>
                            )}
                            {secondSection && (
                                <section>
                                    <h3 className="font-medium mb-2">
                                        {t('what_does_this_mean')}:
                                    </h3>
                                    {sanitizeText(secondSection)}
                                </section>
                            )}
                        </div>

                        <SubmitButton
                            status={familyAppRequest.request.status}
                            onClick={() => handleAgreeClick(att.id)}
                            className="w-full mt-10"
                            buttonTxt={t('agree')}
                        />
                    </main>
                </FamilyWizardLayout.Content>
            </FamilyWizardLayout>
        )
    })
}

export default TaxCommissionAttestation
